import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import useTickets from "../../api/ticket";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  MessageSquare,
  Clock,
  CheckCircle2,
  AlertCircle,
  Filter,
  Check,
  Clock3,
  Building2,
  Mail,
  RefreshCcw,
} from "lucide-react";

const getStatusColor = (status, isDark) => {
  switch (status) {
    case "open":
      return isDark
        ? "bg-blue-500/15 text-blue-400"
        : "bg-blue-50 text-blue-700";
    case "in progress":
      return isDark
        ? "bg-amber-500/15 text-amber-400"
        : "bg-amber-50 text-amber-700";
    case "closed":
      return isDark
        ? "bg-emerald-500/15 text-emerald-400"
        : "bg-emerald-50 text-emerald-700";
    default:
      return isDark
        ? "bg-white/[0.06] text-gray-400"
        : "bg-gray-100 text-gray-600";
  }
};

const getStatusIcon = (status) => {
  switch (status) {
    case "open":
      return <AlertCircle className="h-3 w-3" />;
    case "in progress":
      return <Clock className="h-3 w-3" />;
    case "closed":
      return <CheckCircle2 className="h-3 w-3" />;
    default:
      return null;
  }
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat("nl-NL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const AdminTicketsManager = () => {
  const { isAdmin } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const { getAllTickets, getTicketsByStatus, updateTicketStatus } =
    useTickets();

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [expandedTicket, setExpandedTicket] = useState(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  useEffect(() => {
    fetchTickets();
  }, [statusFilter]);

  // Reset expanded ticket when changing filters
  useEffect(() => {
    setExpandedTicket(null);
  }, [statusFilter]);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      let data;
      if (statusFilter === "all") {
        data = await getAllTickets();
      } else {
        data = await getTicketsByStatus(statusFilter);
      }
      setTickets(data || []);
    } catch (err) {
      console.error("Error fetching tickets:", err);
      setError("Er is een fout opgetreden bij het ophalen van tickets.");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (ticketId, newStatus, e) => {
    e?.stopPropagation();
    setIsUpdatingStatus(true);
    try {
      await updateTicketStatus(ticketId, newStatus);

      // Update the tickets list with the updated ticket
      setTickets(
        tickets.map((ticket) =>
          ticket.id === ticketId
            ? { ...ticket, status: newStatus, updatedAt: new Date() }
            : ticket,
        ),
      );

      setSuccess(
        `Ticket status gewijzigd naar ${
          newStatus === "open"
            ? "Open"
            : newStatus === "in progress"
              ? "In behandeling"
              : "Gesloten"
        }`,
      );

      // Close expanded view if status changed to closed
      if (newStatus === "closed") {
        setTimeout(() => {
          if (expandedTicket === ticketId) {
            setExpandedTicket(null);
          }
        }, 1500);
      }

      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error("Error updating ticket status:", err);
      setError(
        "Er is een fout opgetreden bij het wijzigen van de ticket status.",
      );
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  // Count tickets by status
  const statusCounts = {
    total: tickets.length,
    open: tickets.filter((ticket) => ticket.status === "open").length,
    inProgress: tickets.filter((ticket) => ticket.status === "in progress")
      .length,
    closed: tickets.filter((ticket) => ticket.status === "closed").length,
  };

  const toggleExpandTicket = (ticketId) => {
    if (expandedTicket === ticketId) {
      setExpandedTicket(null);
    } else {
      setExpandedTicket(ticketId);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div
          className={`w-8 h-8 rounded-full border-2 animate-spin ${isDark ? "border-white/[0.08] border-t-white/40" : "border-gray-200 border-t-gray-600"}`}
        />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Totaal", value: statusCounts.total, icon: MessageSquare },
          {
            label: "Open",
            value: statusCounts.open,
            icon: AlertCircle,
            dot: isDark ? "bg-blue-400" : "bg-blue-500",
          },
          {
            label: "In behandeling",
            value: statusCounts.inProgress,
            icon: Clock3,
            dot: isDark ? "bg-amber-400" : "bg-amber-500",
          },
          {
            label: "Opgelost",
            value: statusCounts.closed,
            icon: CheckCircle2,
            dot: isDark ? "bg-emerald-400" : "bg-emerald-500",
          },
        ].map((s) => (
          <div
            key={s.label}
            className={`rounded-xl p-4 ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
          >
            <div className="flex items-center gap-1.5 mb-2">
              <s.icon
                className={`h-3.5 w-3.5 ${isDark ? "text-gray-600" : "text-gray-400"}`}
              />
              <span
                className={`text-[11px] font-medium uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-500"}`}
              >
                {s.label}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {s.dot && <div className={`h-2 w-2 rounded-full ${s.dot}`} />}
              <span
                className={`text-xl font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
              >
                {s.value}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1
            className={`text-[15px] font-semibold tracking-tight ${isDark ? "text-white" : "text-gray-900"}`}
          >
            Ticket management
          </h1>
          <p
            className={`text-[13px] mt-0.5 ${isDark ? "text-gray-500" : "text-gray-500"}`}
          >
            Bekijk en beheer alle support tickets.
          </p>
        </div>
        <button
          onClick={fetchTickets}
          className={`h-8 w-8 rounded-lg flex items-center justify-center transition-colors ${isDark ? "text-gray-500 hover:text-white hover:bg-white/[0.06]" : "text-gray-400 hover:text-gray-700 hover:bg-gray-100"}`}
        >
          <RefreshCcw className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Notifications */}
      {error && (
        <div
          className={`flex items-center gap-2 px-4 py-3 rounded-xl text-[13px] ${isDark ? "bg-red-500/10 border border-red-500/20 text-red-400" : "bg-red-50 border border-red-200 text-red-700"}`}
        >
          <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {success && (
        <div
          className={`flex items-center gap-2 px-4 py-3 rounded-xl text-[13px] ${isDark ? "bg-emerald-500/10 border border-emerald-500/20 text-emerald-400" : "bg-emerald-50 border border-emerald-200 text-emerald-700"}`}
        >
          <Check className="h-3.5 w-3.5 flex-shrink-0" />
          <span>{success}</span>
        </div>
      )}

      {/* Filter */}
      <div className="flex items-center gap-3">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger
            className={`w-[180px] h-9 rounded-lg text-[13px] ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white" : "bg-white border-gray-200 text-gray-900"}`}
          >
            <div className="flex items-center gap-2">
              <Filter className="h-3.5 w-3.5" />
              <SelectValue placeholder="Filter op status" />
            </div>
          </SelectTrigger>
          <SelectContent
            className={isDark ? "bg-[#1a1a1a] border-white/[0.08]" : ""}
          >
            <SelectItem value="all">Alle tickets</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="in progress">In behandeling</SelectItem>
            <SelectItem value="closed">Gesloten</SelectItem>
          </SelectContent>
        </Select>
        <span
          className={`text-[12px] ${isDark ? "text-gray-600" : "text-gray-500"}`}
        >
          {tickets.length} ticket{tickets.length !== 1 ? "s" : ""} gevonden
        </span>
      </div>

      {/* Tickets list */}
      {tickets.length === 0 ? (
        <div
          className={`rounded-xl p-12 text-center ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
        >
          <div
            className={`w-10 h-10 rounded-xl mx-auto mb-3 flex items-center justify-center ${isDark ? "bg-white/[0.04]" : "bg-gray-100"}`}
          >
            <Filter
              className={`h-5 w-5 ${isDark ? "text-gray-600" : "text-gray-400"}`}
            />
          </div>
          <p
            className={`text-[13px] mb-3 ${isDark ? "text-gray-500" : "text-gray-500"}`}
          >
            Geen tickets gevonden met de geselecteerde status.
          </p>
          <button
            onClick={() => setStatusFilter("all")}
            className={`h-8 px-3 rounded-lg text-[12px] font-medium border transition-colors ${isDark ? "border-white/[0.08] text-gray-400 hover:bg-white/[0.04]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
          >
            Toon alle tickets
          </button>
        </div>
      ) : (
        <>
          {/* Mobile compact cards */}
          <div className="sm:hidden space-y-3">
            {tickets.map((ticket, i) => (
              <motion.div
                key={ticket.id}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.02 }}
                className={`rounded-xl overflow-hidden ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
              >
                <div
                  className="p-3 cursor-pointer"
                  onClick={() => toggleExpandTicket(ticket.id)}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`h-11 w-11 rounded-lg flex items-center justify-center shrink-0 ${isDark ? "bg-white/[0.04]" : "bg-gray-100"}`}
                    >
                      <MessageSquare
                        className={`h-5 w-5 ${isDark ? "text-gray-500" : "text-gray-400"}`}
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <span
                        className={`text-[13px] font-semibold block truncate ${isDark ? "text-white" : "text-gray-900"}`}
                      >
                        {ticket.title}
                      </span>
                      <span
                        className={`text-[11px] block truncate ${isDark ? "text-gray-600" : "text-gray-400"}`}
                      >
                        {ticket.companyName} · #{ticket.id}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${getStatusColor(ticket.status, isDark)}`}
                      >
                        {getStatusIcon(ticket.status)}
                        {ticket.status === "open" && "Open"}
                        {ticket.status === "in progress" && "Bezig"}
                        {ticket.status === "closed" && "Dicht"}
                      </span>
                      <svg
                        className={`h-3.5 w-3.5 transition-transform ${expandedTicket === ticket.id ? "rotate-180" : ""} ${isDark ? "text-gray-600" : "text-gray-400"}`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                  </div>
                </div>

                <AnimatePresence>
                  {expandedTicket === ticket.id && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2 }}
                      className={`overflow-hidden border-t ${isDark ? "border-white/[0.04]" : "border-gray-100"}`}
                    >
                      <div className="p-3 space-y-3">
                        {/* Company info */}
                        <div
                          className={`rounded-lg p-3 ${isDark ? "bg-white/[0.02] border border-white/[0.04]" : "bg-gray-50 border border-gray-100"}`}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <Building2
                              className={`h-3.5 w-3.5 ${isDark ? "text-gray-500" : "text-gray-400"}`}
                            />
                            <span
                              className={`text-[11px] font-semibold ${isDark ? "text-gray-300" : "text-gray-700"}`}
                            >
                              Bedrijfsgegevens
                            </span>
                          </div>
                          <div className="space-y-1.5 text-[12px]">
                            <div>
                              <span
                                className={`text-[10px] uppercase tracking-wider block ${isDark ? "text-gray-600" : "text-gray-400"}`}
                              >
                                Naam
                              </span>
                              <span
                                className={
                                  isDark ? "text-white" : "text-gray-900"
                                }
                              >
                                {ticket.companyName}
                              </span>
                            </div>
                            <div>
                              <span
                                className={`text-[10px] uppercase tracking-wider block ${isDark ? "text-gray-600" : "text-gray-400"}`}
                              >
                                E-mail
                              </span>
                              <a
                                href={`mailto:${ticket.companyEmail}`}
                                onClick={(e) => e.stopPropagation()}
                                className={
                                  isDark ? "text-blue-400" : "text-blue-600"
                                }
                              >
                                {ticket.companyEmail}
                              </a>
                            </div>
                          </div>
                        </div>

                        {/* Description */}
                        <div
                          className={`rounded-lg p-3 ${isDark ? "bg-white/[0.02] border border-white/[0.04]" : "bg-gray-50 border border-gray-100"}`}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <MessageSquare
                              className={`h-3.5 w-3.5 ${isDark ? "text-gray-500" : "text-gray-400"}`}
                            />
                            <span
                              className={`text-[11px] font-semibold ${isDark ? "text-gray-300" : "text-gray-700"}`}
                            >
                              Beschrijving
                            </span>
                          </div>
                          <p
                            className={`text-[12px] whitespace-pre-wrap ${isDark ? "text-gray-400" : "text-gray-600"}`}
                          >
                            {ticket.description}
                          </p>
                        </div>

                        {/* Status management */}
                        <div
                          className={`rounded-lg p-3 ${isDark ? "bg-white/[0.02] border border-white/[0.04]" : "bg-gray-50 border border-gray-100"}`}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <Clock
                              className={`h-3.5 w-3.5 ${isDark ? "text-gray-500" : "text-gray-400"}`}
                            />
                            <span
                              className={`text-[11px] font-semibold ${isDark ? "text-gray-300" : "text-gray-700"}`}
                            >
                              Status beheren
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {[
                              { key: "open", label: "Open", Icon: AlertCircle },
                              {
                                key: "in progress",
                                label: "In behandeling",
                                Icon: Clock,
                              },
                              {
                                key: "closed",
                                label: "Gesloten",
                                Icon: CheckCircle2,
                              },
                            ].map(({ key, label, Icon }) => (
                              <button
                                key={key}
                                onClick={(e) =>
                                  handleStatusChange(ticket.id, key, e)
                                }
                                disabled={
                                  isUpdatingStatus || ticket.status === key
                                }
                                className={`h-8 px-3 rounded-lg text-[12px] font-medium inline-flex items-center gap-1.5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed border ${
                                  ticket.status === key
                                    ? getStatusColor(key, isDark) +
                                      (isDark
                                        ? " border-white/[0.06]"
                                        : " border-transparent")
                                    : isDark
                                      ? "border-white/[0.06] text-gray-500 hover:bg-white/[0.04]"
                                      : "border-gray-200 text-gray-500 hover:bg-gray-100"
                                }`}
                              >
                                <Icon className="h-3 w-3" />
                                {label}
                              </button>
                            ))}
                          </div>
                        </div>

                        {ticket.updatedAt &&
                          ticket.updatedAt !== ticket.createdAt && (
                            <p
                              className={`text-[11px] ${isDark ? "text-gray-700" : "text-gray-400"}`}
                            >
                              Laatste update: {formatDate(ticket.updatedAt)}
                            </p>
                          )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>

          {/* Desktop table */}
          <div
            className={`hidden sm:block rounded-xl overflow-hidden ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
          >
            {/* Table header */}
            <div
              className={`grid grid-cols-12 px-5 py-2.5 text-[11px] font-medium uppercase tracking-wider ${isDark ? "text-gray-600 border-b border-white/[0.04]" : "text-gray-400 border-b border-gray-100"}`}
            >
              <div className="col-span-4">Ticket</div>
              <div className="col-span-3">Bedrijf</div>
              <div className="hidden sm:block col-span-2">Datum</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-1"></div>
            </div>

            <div
              className={`divide-y ${isDark ? "divide-white/[0.04]" : "divide-gray-100"}`}
            >
              {tickets.map((ticket) => (
                <div key={ticket.id}>
                  {/* Ticket row */}
                  <div
                    className={`grid grid-cols-12 px-5 py-3.5 cursor-pointer items-center transition-colors ${
                      expandedTicket === ticket.id
                        ? isDark
                          ? "bg-white/[0.02]"
                          : "bg-gray-50/50"
                        : isDark
                          ? "hover:bg-white/[0.02]"
                          : "hover:bg-gray-50/50"
                    }`}
                    onClick={() => toggleExpandTicket(ticket.id)}
                  >
                    <div className="col-span-4">
                      <span
                        className={`text-[13px] font-medium block truncate ${isDark ? "text-white" : "text-gray-900"}`}
                      >
                        {ticket.title}
                      </span>
                      <span
                        className={`text-[11px] ${isDark ? "text-gray-600" : "text-gray-400"}`}
                      >
                        #{ticket.id}
                      </span>
                    </div>

                    <div className="col-span-3 flex items-center gap-2">
                      <div
                        className={`h-6 w-6 rounded-lg flex items-center justify-center ${isDark ? "bg-white/[0.04]" : "bg-gray-100"}`}
                      >
                        <Building2
                          className={`h-3 w-3 ${isDark ? "text-gray-500" : "text-gray-400"}`}
                        />
                      </div>
                      <div className="min-w-0">
                        <span
                          className={`text-[12px] font-medium block truncate ${isDark ? "text-gray-300" : "text-gray-700"}`}
                        >
                          {ticket.companyName}
                        </span>
                        <span
                          className={`text-[11px] block truncate ${isDark ? "text-gray-600" : "text-gray-400"}`}
                        >
                          {ticket.companyEmail}
                        </span>
                      </div>
                    </div>

                    <div
                      className={`hidden sm:block col-span-2 text-[12px] ${isDark ? "text-gray-600" : "text-gray-500"}`}
                    >
                      {formatDate(ticket.createdAt)}
                    </div>

                    <div className="col-span-2">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${getStatusColor(ticket.status, isDark)}`}
                      >
                        {getStatusIcon(ticket.status)}
                        {ticket.status === "open" && "Open"}
                        {ticket.status === "in progress" && "In behandeling"}
                        {ticket.status === "closed" && "Gesloten"}
                      </span>
                    </div>

                    <div className="col-span-1 flex justify-end">
                      <button
                        className={`h-7 w-7 rounded-lg flex items-center justify-center transition-colors ${isDark ? "text-gray-600 hover:text-white hover:bg-white/[0.06]" : "text-gray-400 hover:text-gray-700 hover:bg-gray-100"}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleExpandTicket(ticket.id);
                        }}
                      >
                        <svg
                          className="h-3.5 w-3.5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d={
                              expandedTicket === ticket.id
                                ? "M5 15l7-7 7 7"
                                : "M19 9l-7 7-7-7"
                            }
                          />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Expanded content */}
                  <AnimatePresence>
                    {expandedTicket === ticket.id && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2 }}
                        className={`overflow-hidden border-t ${isDark ? "border-white/[0.04]" : "border-gray-100"}`}
                      >
                        <div className="px-5 py-5">
                          <div
                            className={`ml-6 pl-4 border-l-2 space-y-4 ${isDark ? "border-white/[0.06]" : "border-gray-200"}`}
                          >
                            {/* Company info */}
                            <div
                              className={`rounded-lg p-4 ${isDark ? "bg-white/[0.02] border border-white/[0.04]" : "bg-gray-50 border border-gray-100"}`}
                            >
                              <div className="flex items-center gap-2 mb-3">
                                <Building2
                                  className={`h-3.5 w-3.5 ${isDark ? "text-gray-500" : "text-gray-400"}`}
                                />
                                <span
                                  className={`text-[12px] font-semibold ${isDark ? "text-gray-300" : "text-gray-700"}`}
                                >
                                  Bedrijfsgegevens
                                </span>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-[13px]">
                                <div>
                                  <span
                                    className={`text-[11px] uppercase tracking-wider block mb-0.5 ${isDark ? "text-gray-600" : "text-gray-400"}`}
                                  >
                                    Naam
                                  </span>
                                  <span
                                    className={
                                      isDark ? "text-white" : "text-gray-900"
                                    }
                                  >
                                    {ticket.companyName}
                                  </span>
                                </div>
                                <div>
                                  <span
                                    className={`text-[11px] uppercase tracking-wider block mb-0.5 ${isDark ? "text-gray-600" : "text-gray-400"}`}
                                  >
                                    E-mail
                                  </span>
                                  <a
                                    href={`mailto:${ticket.companyEmail}`}
                                    onClick={(e) => e.stopPropagation()}
                                    className={`${isDark ? "text-blue-400 hover:text-blue-300" : "text-blue-600 hover:text-blue-700"}`}
                                  >
                                    {ticket.companyEmail}
                                  </a>
                                </div>
                              </div>
                            </div>

                            {/* Description */}
                            <div
                              className={`rounded-lg p-4 ${isDark ? "bg-white/[0.02] border border-white/[0.04]" : "bg-gray-50 border border-gray-100"}`}
                            >
                              <div className="flex items-center gap-2 mb-3">
                                <MessageSquare
                                  className={`h-3.5 w-3.5 ${isDark ? "text-gray-500" : "text-gray-400"}`}
                                />
                                <span
                                  className={`text-[12px] font-semibold ${isDark ? "text-gray-300" : "text-gray-700"}`}
                                >
                                  Beschrijving
                                </span>
                              </div>
                              <p
                                className={`text-[13px] whitespace-pre-wrap ${isDark ? "text-gray-400" : "text-gray-600"}`}
                              >
                                {ticket.description}
                              </p>
                            </div>

                            {/* Status management */}
                            <div
                              className={`rounded-lg p-4 ${isDark ? "bg-white/[0.02] border border-white/[0.04]" : "bg-gray-50 border border-gray-100"}`}
                            >
                              <div className="flex items-center gap-2 mb-3">
                                <Clock
                                  className={`h-3.5 w-3.5 ${isDark ? "text-gray-500" : "text-gray-400"}`}
                                />
                                <span
                                  className={`text-[12px] font-semibold ${isDark ? "text-gray-300" : "text-gray-700"}`}
                                >
                                  Status beheren
                                </span>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {[
                                  {
                                    key: "open",
                                    label: "Open",
                                    Icon: AlertCircle,
                                  },
                                  {
                                    key: "in progress",
                                    label: "In behandeling",
                                    Icon: Clock,
                                  },
                                  {
                                    key: "closed",
                                    label: "Gesloten",
                                    Icon: CheckCircle2,
                                  },
                                ].map(({ key, label, Icon }) => (
                                  <button
                                    key={key}
                                    onClick={(e) =>
                                      handleStatusChange(ticket.id, key, e)
                                    }
                                    disabled={
                                      isUpdatingStatus || ticket.status === key
                                    }
                                    className={`h-8 px-3 rounded-lg text-[12px] font-medium inline-flex items-center gap-1.5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed border ${
                                      ticket.status === key
                                        ? getStatusColor(key, isDark) +
                                          (isDark
                                            ? " border-white/[0.06]"
                                            : " border-transparent")
                                        : isDark
                                          ? "border-white/[0.06] text-gray-500 hover:bg-white/[0.04]"
                                          : "border-gray-200 text-gray-500 hover:bg-gray-100"
                                    }`}
                                  >
                                    <Icon className="h-3 w-3" />
                                    {label}
                                  </button>
                                ))}
                              </div>
                            </div>

                            {ticket.updatedAt &&
                              ticket.updatedAt !== ticket.createdAt && (
                                <p
                                  className={`text-[11px] ${isDark ? "text-gray-700" : "text-gray-400"}`}
                                >
                                  Laatste update: {formatDate(ticket.updatedAt)}
                                </p>
                              )}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between text-[12px] px-1">
        <span className={isDark ? "text-gray-600" : "text-gray-500"}>
          Totaal: {tickets.length} {tickets.length === 1 ? "ticket" : "tickets"}
        </span>
        {statusFilter !== "all" && (
          <button
            onClick={() => setStatusFilter("all")}
            className={`text-[12px] transition-colors ${isDark ? "text-gray-500 hover:text-white" : "text-gray-500 hover:text-gray-900"}`}
          >
            Filter wissen
          </button>
        )}
      </div>
    </div>
  );
};

export default AdminTicketsManager;
